import { graphql, useFragment } from "react-relay";
import { combineNumberTaskExecutions } from "./combineNumberTaskExecutions";
import { Section } from "./useWidgetTaskSingleSections";
import { WidgetTaskSingleGroupingMode } from "./__generated__/WidgetTaskSingle_widgetTaskSingle.graphql";

import { useGetUniqueColor } from "./useGetUniqueColor";
import { useGroupNumberTaskExecution_taskExecutionNumber$key } from "./__generated__/useGroupNumberTaskExecution_taskExecutionNumber.graphql";
import { useIntl } from "react-intl";
import { useMemo } from "react";

// 3 steg

// 1. Gruppera taskExecutions till grupper baserat på team assign, dom som inte har team assign stoppar vi i "Other"

// 2. Sortera grupperna från störst till minst

// 3. Ta n av grupperna, grupera ihop resten till en other

export type TaskExecutionGroupItem = {
  numberValue: number | null;
  goals: { lessThan: number | null; greaterThan: number | null } | null;
  groupId: string;
  color: string;
  label: string;
  sectionIds: string[];
  hidden?: boolean;
};
export type TaskExecutionTotalGroup = Omit<
  TaskExecutionGroupItem,
  "groupId" | "sectionIds"
>;

export function useGroupNumberTaskExecution(
  taskExecutionsKeys: useGroupNumberTaskExecution_taskExecutionNumber$key,
  sections: Map<string, Section>,
  maxNumberOfItems = 7,
  grouping: WidgetTaskSingleGroupingMode
): {
  groups: TaskExecutionGroupItem[];
  total: TaskExecutionTotalGroup;
  lastUpdated: string | null;
} {
  const getColor = useGetUniqueColor();
  const _taskExecutions = useFragment(
    graphql`
      fragment useGroupNumberTaskExecution_taskExecutionNumber on TaskExecutionNumber
      @relay(plural: true) {
        id
        sectionId
        numberValue
        updatedAt
        goals {
          lessThan
          greaterThan
        }
      }
    `,
    taskExecutionsKeys
  );

  const taskExecutions = _taskExecutions.filter((te) =>
    sections.has(te.sectionId)
  ); // only sections that we have selected
  const intl = useIntl();
  return useMemo(() => {
    let lastUpdatedAt: null | string = taskExecutions.reduce<null | string>(
      (acc, te) => {
        if (te.updatedAt && (!acc || te.updatedAt > acc)) {
          return te.updatedAt;
        }
        return acc;
      },
      null
    );
    // todo fix color generateion
    const groupItems = (taskExecutions ?? []).reduce((acc, te) => {
      const section = sections.get(te.sectionId);
      if (!section) {
        return acc;
      }
      if (grouping === "SECTION") {
        return [
          ...acc,
          {
            numberValue: te.numberValue,
            goals: te.goals,
            groupId: te.id,
            color: getColor(section.assignedTeam?.color),
            label: section.title,
            sectionIds: [te.sectionId],
          },
        ];
      } else if (grouping === "TEAM" && section.assignedTeam) {
        const team = acc.find(
          ({ groupId }) => groupId === section.assignedTeam!.id
        );

        const combined = team ? combineNumberTaskExecutions([te, team]) : te;
        return [
          ...(team
            ? acc.filter(({ groupId }) => groupId !== team.groupId)
            : acc),
          {
            numberValue: combined.numberValue,
            goals: combined.goals,
            groupId: te.id,
            color: getColor(section.assignedTeam?.color),
            label: section.assignedTeam!.name,
            sectionIds: [te.sectionId, ...(team?.sectionIds ?? [])],
          },
        ];
      }
      return acc;
    }, [] as TaskExecutionGroupItem[]);

    const sortedGroups = groupItems.sort(
      (a, b) => (b.numberValue ?? -Infinity) - (a.numberValue ?? -Infinity)
    );
    // we got sorted abstract groups with labels;
    // now limit the group to desired number of groups
    // put the sections that are not in the groups into a "other" group
    const top6 =
      sortedGroups.length > maxNumberOfItems
        ? sortedGroups.slice(0, maxNumberOfItems - 1)
        : sortedGroups;

    const topSectionIds = top6.flatMap((item) => item.sectionIds);

    const other =
      sortedGroups.length > maxNumberOfItems
        ? combineNumberTaskExecutions(sortedGroups.slice(maxNumberOfItems - 1))
        : null;

    const total = combineNumberTaskExecutions(sortedGroups);

    return {
      lastUpdated: lastUpdatedAt,
      total: {
        label: intl.formatMessage({
          description:
            "WidgetTaskSingle useGroupNumberTaskExecution total label",
          defaultMessage: "Total",
        }),
        color: "#000",
        numberValue: total?.numberValue ?? null,
        goals: total?.goals ?? null,
      },

      groups: [
        ...top6,
        {
          groupId: "other",
          hidden: (other?.numberOfRest ?? 0) === 0,
          label: other?.numberOfRest
            ? intl.formatMessage(
                {
                  description:
                    "WidgetTaskSingle useGroupNumberTaskExecution other label",
                  defaultMessage: "Other ({numberOfOthers})",
                },
                {
                  numberOfOthers: other.numberOfRest,
                }
              )
            : "",
          color: getColor(null),
          numberValue: other?.numberValue ?? null,
          goals: other?.goals ?? null,
          sectionIds: Array.from(sections)
            .map((s) => s[0])
            .filter((sectionId) => !topSectionIds.includes(sectionId)),
        },
      ],
    };
  }, [taskExecutions, maxNumberOfItems, intl, getColor, sections, grouping]);
}
